import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Lock, CheckCircle2, Award, Zap, ChevronDown, Trophy } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { formatNumber } from "@/lib/web3";

const GOLD = "#FFD36A";

// 10-tier color palette (R1..R10) - each rank gets a distinct accent
const RANK_TONES = [
  { color: "#22C55E", dark: "#064E27", text: "#86EFAC" }, // R1 Explorer - green
  { color: "#3B82F6", dark: "#0B2E6B", text: "#93C5FD" }, // R2 Builder - blue
  { color: "#A78BFA", dark: "#3A1C7A", text: "#C4B5FD" }, // R3 Contributor - purple
  { color: "#F59E0B", dark: "#5B3500", text: "#FCD34D" }, // R4 Strategist - amber
  { color: "#06B6D4", dark: "#063E4B", text: "#67E8F9" }, // R5 Architect - cyan
  { color: "#EC4899", dark: "#5A0F36", text: "#F9A8D4" }, // R6 Captain - pink
  { color: "#F97316", dark: "#5A2204", text: "#FDBA74" }, // R7 Commander - orange
  { color: "#10B981", dark: "#053F2E", text: "#6EE7B7" }, // R8 Governor - emerald
  { color: "#FFD36A", dark: "#5A3A00", text: "#FFE9A8" }, // R9 Founder - gold
  { color: "#EF4444", dark: "#5A0A0A", text: "#FCA5A5" }, // R10 Creator - red/crimson
];

const COMMISSION_PCT = {
  1: 2.0, 2: 1.0, 3: 1.0, 4: 1.0, 5: 0.5, 6: 0.25,
  7: 0.5, 8: 0.25, 9: 0.5, 10: 0.25, 11: 0.5, 12: 0.25,
  13: 0.5, 14: 0.25, 15: 0.5, 16: 0.25, 17: 0.5, 18: 0.25,
  19: 0.5, 20: 0.25, 21: 0.5, 22: 1.0, 23: 1.0, 24: 1.0, 25: 1.0,
};

export default function Achievements() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const r = await api.get("/rank/me"); setData(r.data); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="space-y-4"><Skeleton className="h-32 bg-white/5" /><Skeleton className="h-96 bg-white/5" /></div>;

  const ranks = data?.all_ranks || [];
  const unlockedCount = ranks.filter(r => r.unlocked && r.name !== "Unranked").length;
  const currentIdx = ranks.findIndex(r => r.name === data?.rank);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl sm:text-2xl font-semibold">Achievements</h1>
        <p className="text-xs text-[var(--text-3)] mt-1">Track ranks, level commissions, and boosters.</p>
      </div>

      <Tabs defaultValue="ranks" className="space-y-4">
        <TabsList className="tabs-3d grid grid-cols-3 w-full gap-1 h-auto">
          <TabsTrigger value="ranks" data-testid="ach-tab-ranks" className="h-10 font-semibold"><Trophy size={13} className="mr-1.5" />Ranks</TabsTrigger>
          <TabsTrigger value="levels" data-testid="ach-tab-levels" className="h-10 font-semibold"><Award size={13} className="mr-1.5" />Levels</TabsTrigger>
          <TabsTrigger value="booster" data-testid="ach-tab-booster" className="h-10 font-semibold"><Zap size={13} className="mr-1.5" />Booster</TabsTrigger>
        </TabsList>

        <TabsContent value="ranks" className="space-y-4">
          {/* Rank Progress header */}
          <div className="card-3d glow-gold lift-on-hover p-5 relative" data-testid="ach-rank-progress-card">
            <div className="shimmer-line" />
            <div className="absolute -top-6 -right-4 coin-3d-wrap pointer-events-none">
              <div className="coin-3d w-20 h-20 rounded-full" style={{
                background: "radial-gradient(circle at 35% 30%, #FFE9A8 0%, #F3BA2F 45%, #6B4500 100%)",
                boxShadow: "inset -5px -7px 18px rgba(0,0,0,0.45), inset 6px 6px 16px rgba(255,255,255,0.30), 0 10px 22px rgba(243,186,47,0.45)",
              }} />
            </div>
            <div className="relative">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--text-3)]">Rank Progress</div>
                <Badge style={{ background: "rgba(255,211,106,0.16)", color: GOLD, border: "1px solid rgba(255,211,106,0.3)" }} className="hover:bg-transparent">{data?.rank}</Badge>
              </div>
              <div className="mono text-3xl font-semibold gradient-text-anim">Rank {unlockedCount} of 10</div>
              <div className="text-xs text-[var(--text-3)] mt-1">Total deposit: <span className="mono text-[var(--text-2)]">${formatNumber(data?.total_deposit, { minimumFractionDigits: 0 })}</span></div>
            </div>
          </div>

          {/* Rank list - each rank as its own 3D card */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="ach-rank-list">
            {ranks.filter(r => r.name !== "Unranked").map((r, i) => {
              const tone = RANK_TONES[i % RANK_TONES.length];
              const isCurrent = r.name === data?.rank;
              const glowClass = r.unlocked ? (isCurrent ? "glow-gold" : "glow-green") : "";
              return (
                <div
                  key={r.name}
                  data-testid={`rank-card-${r.name.toLowerCase()}`}
                  className={`card-3d lift-on-hover relative p-4 ${glowClass}`}
                  style={{
                    borderColor: r.unlocked ? `${tone.color}55` : "var(--card3d-border)",
                    boxShadow: r.unlocked
                      ? `inset 0 1px 0 rgba(255,255,255,0.06), 0 12px 28px ${tone.color}26, 0 0 0 1px ${tone.color}33`
                      : undefined,
                  }}
                >
                  <div className="shimmer-line" />
                  {/* status pill */}
                  <div className="absolute top-3 right-3">
                    {r.unlocked
                      ? <Badge className="hover:bg-transparent" style={{ background: "rgba(34,197,94,0.16)", color: "#86EFAC", border: "1px solid rgba(34,197,94,0.35)" }}>Unlocked</Badge>
                      : <Badge className="hover:bg-transparent" style={{ background: "rgba(255,255,255,0.04)", color: "var(--text-3)", border: "1px solid var(--card3d-border)" }}><Lock size={10} className="mr-1" />Locked</Badge>}
                  </div>

                  {/* Tier medallion - classic colorful coin */}
                  <div className="flex items-center gap-3">
                    <div className="relative shrink-0">
                      {/* Outer metallic ring - colorful for every tier */}
                      <div
                        className="w-16 h-16 rounded-full flex items-center justify-center relative"
                        style={{
                          background: `conic-gradient(from 210deg, ${tone.color} 0deg, #FFFFFF 60deg, ${tone.color} 120deg, ${tone.dark} 200deg, ${tone.color} 280deg, #FFFFFF 340deg, ${tone.color} 360deg)`,
                          boxShadow: r.unlocked
                            ? `0 6px 16px ${tone.color}88, 0 0 0 1px rgba(0,0,0,0.25), inset 0 1px 2px rgba(255,255,255,0.7)`
                            : `0 4px 12px ${tone.color}44, 0 0 0 1px rgba(0,0,0,0.3), inset 0 1px 2px rgba(255,255,255,0.5)`,
                          filter: r.unlocked ? "none" : "saturate(0.85)",
                        }}
                      >
                        {/* Inner colored gem - always colored per tier */}
                        <div
                          className="w-[52px] h-[52px] rounded-full flex items-center justify-center relative overflow-hidden"
                          style={{
                            background: `radial-gradient(circle at 32% 28%, #FFFFFF 0%, ${tone.color} 28%, ${tone.color} 55%, ${tone.dark} 100%)`,
                            boxShadow: r.unlocked
                              ? `inset 0 2px 4px rgba(255,255,255,0.65), inset 0 -4px 8px ${tone.dark}, inset 0 0 22px ${tone.color}66`
                              : `inset 0 2px 4px rgba(255,255,255,0.45), inset 0 -4px 8px ${tone.dark}, inset 0 0 16px ${tone.color}33`,
                            border: `1px solid ${tone.dark}`,
                            filter: r.unlocked ? "none" : "saturate(0.8) brightness(0.92)",
                          }}
                        >
                          {/* Glossy top sheen */}
                          <div
                            className="absolute top-[3px] left-[6px] w-7 h-3 rounded-full pointer-events-none"
                            style={{
                              background: "linear-gradient(180deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0) 100%)",
                              filter: "blur(1px)",
                              opacity: r.unlocked ? 0.9 : 0.7,
                            }}
                          />
                          {/* Tier label - classic serif */}
                          <span
                            className="font-bold tracking-tight relative"
                            style={{
                              fontFamily: '"Playfair Display", "Source Serif Pro", Georgia, serif',
                              fontSize: "19px",
                              lineHeight: 1,
                              color: "#FFFFFF",
                              textShadow: `0 1px 0 ${tone.dark}, 0 0 8px rgba(255,255,255,0.55), 0 2px 4px rgba(0,0,0,0.55)`,
                              letterSpacing: "0.5px",
                            }}
                          >
                            R{i + 1}
                          </span>
                          {/* Bottom rim shadow */}
                          <div
                            className="absolute bottom-0 left-0 right-0 h-3 pointer-events-none"
                            style={{
                              background: "linear-gradient(0deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0) 100%)",
                            }}
                          />
                        </div>
                      </div>
                      {/* Soft glow ring for unlocked only */}
                      {r.unlocked && (
                        <div
                          className="absolute inset-0 rounded-full pointer-events-none animate-pulse"
                          style={{
                            boxShadow: `0 0 22px 3px ${tone.color}88`,
                          }}
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-base font-semibold truncate" style={{ color: tone.text }}>{r.name}</div>
                      <div className="text-[11px] text-[var(--text-3)] mt-0.5">{isCurrent ? "Current rank" : (r.unlocked ? "Achievement unlocked" : "Locked tier")}</div>
                    </div>
                  </div>

                  {/* Threshold + progress */}
                  <div className="mt-4 umakant-panel p-3 flex items-center justify-between gap-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--text-3)]">Required</div>
                      <div className="mono text-base font-semibold" style={{ color: tone.text }}>${formatNumber(r.threshold || r.pv_required, { minimumFractionDigits: 0 })}</div>
                    </div>
                    {r.unlocked ? (
                      <div className="flex items-center gap-1 text-[var(--success)]"><CheckCircle2 size={18} /></div>
                    ) : (
                      <div className="text-right">
                        <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--text-3)]">Your Deposit</div>
                        <div className="mono text-xs text-[var(--text-2)]">${formatNumber(data?.total_deposit, { minimumFractionDigits: 0 })}</div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="levels" className="space-y-4">
          <div className="card-3d glow-purple lift-on-hover p-5 relative" data-testid="ach-level-progress-card">
            <div className="shimmer-line" />
            <div className="relative">
            <div className="flex items-center justify-between mb-4">
              <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--text-3)]">Level Progress</div>
              <Badge className="hover:bg-transparent" style={{ background: "rgba(124,58,237,0.16)", color: "#C4B5FD", border: "1px solid rgba(124,58,237,0.35)" }}>25 Levels</Badge>
            </div>
            <div className="grid grid-cols-5 gap-2">
              {Array.from({ length: 25 }).map((_, i) => {
                const lvl = i + 1;
                const pct = COMMISSION_PCT[lvl];
                // Tier: high (>=1%) gold, mid (0.5%) purple, low (0.25%) blue
                const tier = pct >= 1 ? "gold" : (pct >= 0.5 ? "purple" : "blue");
                const palette = {
                  gold:   { bg1: "#FBBF24", bg2: "#D97706", border: "#92400E", label: "rgba(255,255,255,0.85)" },
                  purple: { bg1: "#A78BFA", bg2: "#7C3AED", border: "#5B21B6", label: "rgba(255,255,255,0.85)" },
                  blue:   { bg1: "#60A5FA", bg2: "#2563EB", border: "#1E3A8A", label: "rgba(255,255,255,0.85)" },
                }[tier];

                return (
                  <div
                    key={lvl}
                    className="relative aspect-square rounded-xl flex flex-col items-center justify-center px-1"
                    style={{
                      background: `linear-gradient(180deg, ${palette.bg1} 0%, ${palette.bg2} 100%)`,
                      border: `1px solid ${palette.border}`,
                      boxShadow: `inset 0 1px 0 rgba(255,255,255,0.25), 0 2px 4px rgba(0,0,0,0.18)`,
                    }}
                    data-testid={`level-${lvl}-cell`}
                  >
                    <div
                      className="leading-none font-semibold"
                      style={{
                        fontSize: "11px",
                        color: "rgba(255,255,255,0.9)",
                        letterSpacing: "0.04em",
                      }}
                    >
                      L{lvl}
                    </div>
                    <div
                      className="mono font-bold leading-none mt-1"
                      style={{
                        fontSize: "13px",
                        color: "#FFFFFF",
                        letterSpacing: "-0.02em",
                      }}
                    >
                      {pct}%
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Legend */}
            <div className="mt-4 flex flex-wrap gap-3 text-[10px] uppercase tracking-[0.14em] text-[var(--text-3)]">
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{ background: "linear-gradient(180deg,#FBBF24,#D97706)", border: "1px solid #92400E" }} /> 1% – 2%</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{ background: "linear-gradient(180deg,#A78BFA,#7C3AED)", border: "1px solid #5B21B6" }} /> 0.5%</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{ background: "linear-gradient(180deg,#60A5FA,#2563EB)", border: "1px solid #1E3A8A" }} /> 0.25%</span>
            </div>
            <div className="text-[11px] text-[var(--text-3)] mt-3">Commission earned at each upline level when a downline stakes.</div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="booster" className="space-y-4">
          <div className="card-3d lift-on-hover p-6 text-center" data-testid="ach-booster-card">
            <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center mb-3" style={{ background: "rgba(255,211,106,0.16)", color: GOLD, border: "1px solid rgba(255,211,106,0.3)" }}>
              <Zap size={26} />
            </div>
            <div className="font-semibold mb-1">Booster Engine</div>
            <div className="text-xs text-[var(--text-3)] mb-5">Activate boosters to multiply your daily yield</div>
            <div className="grid grid-cols-2 gap-3">
              <div className="umakant-panel p-4">
                <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--text-3)]">Normal Bonus</div>
                <div className="mono text-2xl font-semibold mt-1" style={{ color: GOLD }}>1.00%</div>
                <div className="text-[10px] text-[var(--text-3)]">per day</div>
              </div>
              <div className="umakant-panel p-4" style={{ borderColor: "rgba(124,58,237,0.4)" }}>
                <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--text-3)]">Booster Bonus</div>
                <div className="mono text-2xl font-semibold mt-1" style={{ color: "#C4B5FD" }}>1.30%</div>
                <div className="text-[10px] text-[var(--text-3)]">per day</div>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
